import React, { memo, useContext } from 'react';
import { connect } from 'react-redux';

import { Translate } from 'src/components/translate';
import { AppContext } from 'src/components/app-context';
import { List } from '@prisjakt/ui/list';
import { TextLink } from 'src/components/text-link';

import { AppState } from '../../reducers';
import Viewport from '../../components/viewport';
import ChangeMarket from './components/change-market';

import { Site, SocialNetwork, Url } from './reducers/types';
import { Pages } from '../../globals/actions/types';

import { SIDEBAR_STATE } from '../sidebar/constants';

import StaticIntercom from '../../components/static-intercom';
import ClassicSwitch from '../../components/classic-switch';

import * as S from './styles';
import InfoPages from 'src/areas/footer/components/info-pages';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import {
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
} from '@prisjakt/ui/icon';
import { PrismicArticle } from 'src/types';
import {
  getFooterColumns,
  getNewFooterColumns,
  getPageMap,
} from './footerPages';

type StateProps = {
  socialNetworks: SocialNetwork[];
  current: string;
  sites: Site[];
  pages?: Pages;
  prismicArticles?: PrismicArticle[];
  url?: Url;
  sidebarOpen: boolean;
};

type OwnProps = {};

type Props = OwnProps & StateProps;

const Footer = ({
  socialNetworks,
  current,
  sites,
  pages,
  url,
  sidebarOpen,
  prismicArticles,
}: Props) => {
  const {
    t,
    market,
    market: { infoPageMap },
    linkTo,
  } = useContext(AppContext);

  const pageMap = getPageMap(infoPageMap, url, market, linkTo, t, pages);
  const infoPageColumns =
    market.code === 'se'
      ? getNewFooterColumns(pageMap, t, prismicArticles)
      : getFooterColumns(pageMap, t);

  return (
    <S.Footer sidebarOpen={sidebarOpen}>
      <S.TopSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.NewsLetterIllustration
            src={`${market.cdn}/g/rfe/illustrations/newsletter-illustration-188x102px.png`}
            srcSet={`${market.cdn}/g/rfe/illustrations/newsletter-illustration-188x102px.png 1x, ${market.cdn}/g/rfe/illustrations/newsletter-illustration-376x202px.png 2x`}
            alt="Footer newsletter illustration"
          />

          <S.NewsletterSignup
            title={t('footer', 'newsletter.title')}
            stacked
            variant="secondary"
          />
        </S.Content>
      </S.TopSection>

      <S.MidSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.Columns>
            {pages && <InfoPages columns={infoPageColumns} />}

            <S.Download>
              <StaticIntercom
                customButton={
                  <S.FeedbackButton
                    fullWidth
                    containerProps={{
                      title: t('footer', 'link.contact'),
                      'data-test': 'FeedbackButton',
                    }}
                  >
                    <S.ButtonText>{t('footer', 'link.contact')}</S.ButtonText>
                  </S.FeedbackButton>
                }
              />
              <S.DownloadTitle>
                {t('footer', 'mobile-apps.title')}
              </S.DownloadTitle>
              <S.DownloadIcons
                dangerouslySetInnerHTML={{
                  __html: t('footer', 'download.text.md'),
                }}
              />
            </S.Download>
          </S.Columns>
        </S.Content>
      </S.MidSection>

      <S.BottomSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.LogoWrapper>
            <S.Logo
              styling="v2-dark"
              market={market.code}
              companyName={t('meta', 'site.name')}
            />

            <Viewport tablet desktop>
              <div>
                <p>
                  {t('footer', 'info.copyright', {
                    year: new Date().getFullYear(),
                  })}
                </p>
              </div>
            </Viewport>

            <Viewport mobile>
              <div>
                <p>
                  {t('footer', 'info.copyright', {
                    year: new Date().getFullYear(),
                  })}
                </p>
              </div>
            </Viewport>
          </S.LogoWrapper>

          <S.ClassicSwitchWrapper>
            <Viewport tablet desktop>
              <ClassicSwitch utmMedium="header">
                <S.ClassicSwitchLink>
                  <Translate domain="header">
                    login.classic-link-word-wrap
                  </Translate>
                </S.ClassicSwitchLink>
              </ClassicSwitch>
            </Viewport>
            {market.schibstedOtherSitesLinks && (
              <Viewport tablet desktop>
                {t('footer', 'info.check-it-out')}:{' '}
                {market.schibstedOtherSitesLinks.map(
                  ({ name, siteUrl }) =>
                    url && (
                      <TextLink to={siteUrl} isExternal>
                        {name}&nbsp;
                      </TextLink>
                    )
                )}
              </Viewport>
            )}
          </S.ClassicSwitchWrapper>

          <S.SocialIcons>
            {socialNetworks.map(
              ({ name, link }) =>
                link && (
                  <S.SocialIcon
                    key={name}
                    title={name}
                    href={link}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                  >
                    {name === 'instagram' && (
                      <IconSocialMediaInstagram size={27} color="base500" />
                    )}
                    {name === 'facebook' && (
                      <IconSocialMediaFacebook size={27} color="base500" />
                    )}
                    {name === 'twitter' && (
                      <IconSocialMediaTwitter size={27} color="base500" />
                    )}
                  </S.SocialIcon>
                )
            )}
          </S.SocialIcons>

          <S.Sites>
            <ChangeMarket sites={sites} current={current} />
          </S.Sites>
        </S.Content>

        {market.showSchibstedCoBranding && (
          <S.Content sidebarOpen={sidebarOpen}>
            <List container={S.CoBranding}>
              <img
                alt="Schibsted"
                src={`${market.cdn}/g/rfe/logos/schibsted/schibsted_dust_black.png`}
                width="96"
              />
              <BodySmallText style={{ lineHeight: 1 }}>
                {`${t('tracking-settings', 'co-branding.part-of', {
                  companyName: t('meta', 'company.name'),
                })} ${t('tracking-settings', 'co-branding.data-controller')} `}
                <S.CoBrandingLink
                  to="/privacy-settings"
                  // This link is nofollow to avoid it getting too much of the internal
                  // pagerank (since it occurs on every page).
                  rel="nofollow"
                >
                  {t('tracking-settings', 'co-branding.read-more')}
                </S.CoBrandingLink>
              </BodySmallText>
            </List>
          </S.Content>
        )}
      </S.BottomSection>
    </S.Footer>
  );
};

const mapStateToProps = (state: AppState) => ({
  socialNetworks: state.footer.socialNetworks,
  current: state.footer.current,
  sites: state.footer.sites,
  pages: state.globals.infoPages,
  url: state.footer.url,
  sidebarOpen: state.areas.sidebar.tab !== SIDEBAR_STATE.NONE,
  prismicArticles: state.footer.articles,
});

export default connect(mapStateToProps, {})(memo(Footer));
